import MultiChoice from "./MultiChoice"
import PostalCode from "./PostalCode"
import SingleChoice from "./SingleChoice"
import TextChoice from "./TextChoice"

import { formatNumber } from "lib/NumberHelper"
import InputFieldWithButton from "./InputFieldWithButton"
import EmailAddressField from "./EmailAddressField"

// A constant representing that the object the values are added to
// is the root object of the payload
export const ROOT_OBJECT_NAME = "_root_"
export const SELL_YOUR_CAR_STEPS_PREFIX = "sell_your_car_"

const defaultStepConfig = {
  apiCall: true, // Whether it triggers an API call
  apiIds: null,
  apiName: null, // The name of the step in the API, if different than stepName
  completeAutomatically: true, // Whether the step is completed automatically
  completed: false, // Whether the step is completed (state-managed)
  component: SingleChoice, // What component to use inside the step
  componentProps: {}, // Props to pass to step component
  options: null, // The options that are available (also state-managed)

  // The name of the object that will be generated when the CarAssemblage is finalized
  // In the end there can be multiple objects generated, each step belonging to either one
  // You can use ROOT_OBJECT_NAME if multiple objects are to be generated by one step
  selectedObjectName: "estimation_car",

  selected: null, // What option is selected (object with keys and values) (state-managed)
  selectedFromCar: null, // How to get the `selected` values from a car object
  selectedFromCarDelayed: null, // How to get the `selected` values from a car object, but done as soon as the options are ready (alternative to selectedFromCar)
  valueToLabelMapper: null, // How to map a value to a label for displaying the option
}

const defaultStepNames = [
  "brand",
  "model",
  "year",
  "fuel",
  "engine_size_and_hp",
  "equipment_variant",
  "body",
  "has_auto_gear_and_has_4wd",
  "color",
  "equipment",
  "car_condition",
  "km",
  `${SELL_YOUR_CAR_STEPS_PREFIX}postcode`,
  "email_address",
]

// All the steps are configured here
export const stepsConfigForSellYourCar = {
  brand: ({ brandAndModelOptions }) => ({
    apiCall: false,
    options: brandAndModelOptions,
    valueToLabelMapper: (value) => value.brand,
    selectedFromCar: (car, options) => {
      if (!car.brand) return
      const brandAndModels = options.all.find(([brand]) => brand === car.brand)
      if (!brandAndModels) return
      let [brand] = brandAndModels
      return brand ? { brand } : {}
    },
  }),
  model: ({ brandAndModelOptions }) => ({
    apiName: "brand_and_model_name",
    options: brandAndModelOptions,
    valueToLabelMapper: (value) => value.car_model,
    selectedFromCar: (car, options) => {
      if (!car.brand) return
      const brandAndModels = options.all.find(([brand]) => brand === car.brand)
      if (!brandAndModels) return
      let [brand, models] = brandAndModels
      return brand && models.includes(car.car_model)
        ? { car_model: car.car_model }
        : {}
    },
  }),
  year: () => ({
    valueToLabelMapper: (value) => value.year.toString(),
    selectedFromCar: ({ year }) => year && { year },
  }),
  fuel: ({ labels }) => ({
    valueToLabelMapper: (value) => labels.fuelValues[value.fuel],
    selectedFromCar: ({ fuel }) => fuel && { fuel },
  }),
  engine_size_and_hp: ({ labels }) => ({
    valueToLabelMapper: (value) =>
      `${value.engine_size ? value.engine_size + " " : ""}${value.hp} ${
        labels.hpUnit
      }`,
    selectedFromCar: ({ engine_size, hp }) =>
      (engine_size || hp) && { engine_size, hp },
  }),
  equipment_variant: ({ labels }) => ({
    apiCall: false,
    valueToLabelMapper: (value) =>
      value.equipment_variant
        ? value.equipment_variant
        : labels.blankEquipmentVariant,
    selectedFromCar: ({ equipment_variant }) =>
      equipment_variant && { equipment_variant },
  }),
  body: ({ labels }) => ({
    apiName: "body_and_doors",
    valueToLabelMapper: (value) =>
      `${labels.bodyValues[value.body]}${
        value.doors ? ` ${value.doors} ${labels.doors}` : ""
      }`,
    selectedFromCar: ({ body, doors }) => {
      if (body && !doors) return { body }
      if (body && doors) return { body, doors }
      if (!body && doors) return { doors }

      return {}
    },
  }),
  has_auto_gear_and_has_4wd: ({ labels }) => ({
    valueToLabelMapper: (value) =>
      `${(value.has_4wd ? labels.fourWheelDrive : "") + " "}${
        value.has_auto_gear ? labels.gears.automatic : labels.gears.manual
      }`,
    selectedFromCar: ({ has_auto_gear, has_4wd }) =>
      (has_auto_gear != null || has_4wd != null) && { has_auto_gear, has_4wd },
  }),
  color: ({ labels }) => ({
    apiCall: false,
    completeAutomatically: false,
    options: labels.colors,
    component: SingleChoice,
    selectedObjectName: "trade_in_valuation",
    valueToLabelMapper: (value) =>
      (labels.colors.find((item) => item.value === value) || {}).label,
  }),
  equipment: ({ labels }) => ({
    id: "equipment",
    apiIds: ["common_equipment", "additional_equipment"],
    apiCall: false,
    completeAutomatically: true,
    component: MultiChoice,
    // We expect the values to be objects with 1 key e.g. {has_abs: true}
    // We'll need to use the key as a translation key and get the translation
    valueToLabelMapper: (value) => {
      const keys = Object.keys(value)
      if (keys.length === 0) {
        return labels.headlines.equipment
      } else {
        return Object.keys(value)
          .reduce((acc, key) => {
            if (value[key]) {
              acc.push(labels.equipmentLabels[key].label)
            }
            return acc
          }, [])
          .join(", ")
      }
    },
    initiallyPreselected: (options) => {
      const initiallyPreselected = {}
      // Automatically preselect all "common equipment" options
      if (options[0].options.length) {
        options[0].options
          .reduce((acc, e) => acc.concat(e), [])
          .forEach(({ value }) => {
            for (let key in value) {
              initiallyPreselected[key] = value[key]
            }
          })
      }
      return initiallyPreselected
    },
    selectedFromCarDelayed: (car, options) => {
      const selected = {}
      options
        .map(({ options }) => options)
        .reduce((acc, e) => acc.concat(e), []) // flat
        .forEach(({ value }) => {
          for (let key in value) {
            if (value.hasOwnProperty(key) && car[key]) selected[key] = car[key]
          }
        })
      return selected
    },
  }),
  car_condition: ({ labels, conditionOptions, conditionAttributes }) => {
    const options = conditionOptions.length && conditionOptions
    const optionsWithoutDesc = options.map((option) => {
      const { description, shortDescription, ...rest } = option
      return rest
    })
    const nestedMultiChoices =
      conditionAttributes.length > 0
        ? {
            choices: conditionAttributes,
            objectName: "estimation_car",
          }
        : null

    return {
      apiCall: false,
      completeAutomatically: false,
      options: [...optionsWithoutDesc].reverse(),
      component: SingleChoice,
      componentProps: {
        fullWidthOptions: true,
        nestedMultiChoices,
        showConfirmButtonAfterSelection: true,
      },
      // In order to support conditionAttributes for estimation_car object
      selectedObjectName: ROOT_OBJECT_NAME,
      valueToLabelMapper: (value) => {
        return `${labels.condition}: ${
          options[value.trade_in_valuation.buyer_car_condition_grade - 1].label
        }`
      },
    }
  },
  km: ({ labels, locale }) => ({
    apiCall: false,
    completeAutomatically: false,
    options: { km: "" },
    component: InputFieldWithButton,
    componentProps: { type: "number", valuePropertyName: "km" },
    valueToLabelMapper: (value) =>
      `${formatNumber(value.km, locale)} ${labels.mileageUnit}`,
    selectedFromCar: ({ km }) => (km ? { km } : null),
  }),
  [`${SELL_YOUR_CAR_STEPS_PREFIX}postcode`]: ({
    postcodesForTypeaheadUrl,
  }) => ({
    apiCall: false,
    completeAutomatically: false,
    component: PostalCode,
    options: [""], // This is only needed to make the component render
    componentProps: { postcodesForTypeaheadUrl },
    valueToLabelMapper: (value) => value.name,
  }),
  email_address: () => ({
    apiCall: false,
    completeAutomatically: false,
    selectedObjectName: "trade_in_valuation",
    options: { buyer_email: "" },
    component: EmailAddressField,
    componentProps: { type: "text" },
    valueToLabelMapper: (value) => `${value.buyer_email}`,
  }),
}

// Returns the step configs in the desired order (by specifying stepNames property)
export default ({
  brandAndModelOptions,
  labels,
  conditionOptions,
  conditionAttributes,
  intendedCarChangePeriods,
  departmentOptions,
  stepNames = defaultStepNames,
  locale,
  postcodesForTypeaheadUrl,
  departmentParamName,
  removeColorsStep,
}) => {
  if (!(conditionOptions && conditionOptions.length)) {
    // Remove car_condition step in case there are no options (not enabled by config)
    stepNames = stepNames.filter((stepName) => stepName !== "car_condition")
  }
  if (!(departmentOptions && departmentOptions.length)) {
    // Remove department step in case there are no options (not a site-wide config)
    stepNames = stepNames.filter((stepName) => stepName !== "department")
  }
  if (removeColorsStep) {
    stepNames = stepNames.filter((stepName) => stepName !== "color")
  }

  return [
    ...stepNames.map((stepName) => ({
      ...defaultStepConfig,
      stepName,
      ...stepsConfigForSellYourCar[stepName]({
        brandAndModelOptions,
        labels,
        conditionOptions,
        conditionAttributes,
        intendedCarChangePeriods,
        departmentOptions,
        locale,
        postcodesForTypeaheadUrl,
        departmentParamName,
      }),
      valid: true,
    })),
  ]
}